import { requests } from 'api-services';
import { NewProduct, UpdatedProduct } from 'api-services/product-requests';
import produce from 'immer';
import { useEffect } from 'react';
import { RequestStatus } from 'react-app-env';
import { atom, useRecoilState } from 'recoil';
import { Product } from 'utils/Product';

type CacheStatus = RequestStatus | 'Stale';

const productsAtom = atom<{[Id: string]: Product} | undefined>({
  key: 'productAtom',
  default: undefined,
});

const productCacheStatusAtom = atom<CacheStatus>({
  key: 'productCacheStatusAtom',
  default: 'Loading',
});

const mapProductArrayToRecord = (products: Product[]): {[Id: string]: Product} => {
  return products.reduce((prev, current) => ({ ...prev, [current.id]: current }), {});
};

export const useProducts = () => {
  const [products, setProducts] = useRecoilState(productsAtom);
  const [status, setStatus] = useRecoilState(productCacheStatusAtom);

  useEffect(() => {
    if (status === 'Ok') setStatus('Stale');
    requests.product.getAll()
      .then(p => {
        setProducts(mapProductArrayToRecord(p));
        setStatus('Ok');
      })
      .catch(() => setStatus('Error'));
  }, []);

  return { status, products: products && Object.values(products) };
};

export const useVisibleProducts = () =>  {
  const { products, ...rest } = useProducts();
  return {
    products: products?.filter(p => p.visible),
    ...rest,
  };
};

export const useProductOperations = () => {
  const [products, setProducts] = useRecoilState(productsAtom);

  return {
    createProduct: async (newProduct: NewProduct) => {
      const product = await requests.product.post(newProduct);
      if (products) {
        setProducts(produce(products, draft => {
          draft[product.id] = product;
        }));
      }
      return product;
    },
    updateProduct: async (updatedProduct: UpdatedProduct) => {
      const product = await requests.product.put(updatedProduct);
      if (products) {
        setProducts(produce(products, draft => {
          draft[product.id] = product;
        }));
      }
      return product;
    },
    deleteProduct: async (productId: string) => {
      const product = await requests.product.delete(productId);
      if (products) {
        setProducts(produce(products, draft => {
          delete draft[productId];
        }));
      }
      return product;
    },
    setProductVisibility: async (product: Product, visibility: boolean) => {
      const newProduct = await requests.product.setVisibility(product, visibility);
      if (products) {
        setProducts(produce(products, draft => {
          draft[product.id].visible = visibility;
        }));
      }
      return newProduct;
    },
  };
};
